import React, { useState, useEffect } from "react";
import TextArea from "@/components/ui/form-input/TextArea";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { useWorkerCreation } from "@/context/WorkerCreationContext";
import DynamicListInput from "@/components/ui/dynamic-list-input";
import { useNameGenerator } from "@/hooks/useNameGenerator";
import EditableName from "@/components/ui/EditableName";
import EditableJobTitle from "@/components/ui/EditableJobTitle";

export default function CreateWorkerDeployWorker({
  isEditMode,
}: {
  isEditMode: boolean;
}) {
  const { workerData, updateWorkerData, selectedTemplate } =
    useWorkerCreation();
  const { currentName, updateJobTitleFromName, markJobTitleAsManuallyEdited } =
    useNameGenerator();

  // Prefill form from selected template on mount or when template changes
  // Only if we're not in edit mode AND the data hasn't been customized yet
  useEffect(() => {
    if (selectedTemplate && !isEditMode) {
      // Check if the worker data is still in default state (not customized by user)
      const isDefaultData =
        workerData.name === "Worker" &&
        workerData.jobTitle === "" &&
        workerData.bio === "" &&
        workerData.rules === "" &&
        (workerData.instructions === "" ||
          workerData.instructions === undefined);

      // Only overwrite if data is still in default state
      if (isDefaultData) {
        updateWorkerData({
          name: selectedTemplate.name,
          jobTitle: selectedTemplate.default_job_title,
          bio: selectedTemplate.default_bio,
          rules: selectedTemplate.default_rules.join("\n"),
          instructions:
            (selectedTemplate as any).default_instructions?.join("\n") || "",
        });
      }
    }
    // Only run when selectedTemplate changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, isEditMode]);

  const handleNameChange = (name: string) => {
    updateWorkerData({ name });
    // Auto-generate job title when name changes (if not manually edited)
    updateJobTitleFromName();
  };

  const handleJobTitleChange = (jobTitle: string) => {
    updateWorkerData({ jobTitle });
    // Mark as manually edited when user changes the job title
    markJobTitleAsManuallyEdited();
  };

  const handleBioChange = (bio: string) => {
    updateWorkerData({ bio });
  };

  const handleInstructionsChange = (instructions: string) => {
    updateWorkerData({ instructions });
  };

  const handleRulesChange = (rules: string[]) => {
    updateWorkerData({ rules: rules.join("\n") });
  };

  const currentRules = Array.isArray(workerData.rules)
    ? workerData.rules
    : (workerData.rules || "").split("\n");

  // if (!selectedTemplate) {
  //   return (
  //     <div className="text-center py-12 text-red-500">
  //       No template selected. Please go back and select a template to continue.
  //     </div>
  //   );
  // }

  return (
    <div>
      <h2 className="text-xl font-medium text-slate-800 dark:text-slate-200">
        Worker Setup
      </h2>
      <p className="text-slate-500 dark:text-slate-400 mt-1">
        Customize a worker to match your specific needs.
      </p>

      <div className="mt-8 space-y-6 text-white/80">
        {/* Worker Avatar and Name Section */}
        <div className="flex items-center gap-4 p-4 px-0  ">
          <WorkerAvatar seed={currentName} size={64} animated />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                <EditableName
                  value={currentName}
                  onChange={handleNameChange}
                  className="text-lg font-semibold text-slate-800 dark:text-slate-200"
                />
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <p className="font-mono text-slate-600 dark:text-slate-400">
                <EditableJobTitle
                  value={workerData.jobTitle}
                  onChange={handleJobTitleChange}
                  onBlur={updateJobTitleFromName}
                  onStartEdit={markJobTitleAsManuallyEdited}
                />
              </p>
            </div>
          </div>
        </div>

        <TextArea
          label="Persona & Bio"
          id="persona-bio"
          placeholder="Enter your workers persona and bio"
          rows={4}
          value={workerData.bio}
          onChange={(e) => handleBioChange(e.target.value)}
        />

        <TextArea
          label="Instructions"
          id="instructions"
          placeholder="Provide custom instructions for your worker"
          rows={4}
          value={workerData.instructions || ""}
          onChange={(e) => handleInstructionsChange(e.target.value)}
        />

        <DynamicListInput
          label="Custom Rules"
          items={currentRules}
          onChange={handleRulesChange}
          placeholder="Enter a custom rule"
          addButtonLabel="Add Rule"
        />
      </div>
    </div>
  );
}
